import { useState } from "react";
import { CardElement } from "@stripe/react-stripe-js";
import type { StripeCardElementChangeEvent } from "@stripe/stripe-js";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";

import { inputStyles, Label, Message } from "@/shared/ui";
import { tw } from "@/shared/utils";

interface CreditCardInputProps {
  id: string;
  className?: string;
  compact?: boolean;
  label?: string;
  message?: string;
  error?: string;
  onChange?: (hasCreditCard: boolean) => void;
  onBlur?: VoidFunction;
}

const CreditCardInput = ({
  id,
  compact,
  label,
  message,
  error,
  onChange,
  onBlur,
}: CreditCardInputProps) => {
  const [stripeErrorMessage, setStripeErrorMessage] = useState("");
  const [iframeFocus, setIframeFocus] = useState(false);

  const handleChange = (e: StripeCardElementChangeEvent) => {
    onChange?.(!e.empty);
    setStripeErrorMessage(e.error?.message ?? "");
  };

  const errorMessage = stripeErrorMessage || error;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>

      <CardElement
        id={id}
        options={{
          style: {
            base: {
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "14px",
              color: "#292929",
              "::placeholder": {
                fontWeight: "300",
                color: "#656565",
              },
            },
          },
        }}
        className={tw(
          inputStyles().root({ className: "block! bg-white! py-3" }),
          !!errorMessage && "border-red-700",
          iframeFocus &&
            "border-neutral-500 ring-2 ring-secondary-200 ring-offset-1",
        )}
        onChange={handleChange}
        onFocus={() => setIframeFocus(true)}
        onBlur={() => {
          setIframeFocus(false);
          onBlur?.();
        }}
      />

      {(!compact || !!message || !!errorMessage) && (
        <Message message={message} error={errorMessage} />
      )}
    </div>
  );
};

interface HookedCreditCardInputProps<TFieldValues extends FieldValues>
  extends CreditCardInputProps {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
}
export const HookedCreditCardInput = <TFieldValues extends FieldValues>({
  name,
  control,
  ...props
}: HookedCreditCardInputProps<TFieldValues>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const { onChange } = field;
        return <CreditCardInput {...props} onChange={onChange} />;
      }}
    />
  );
};
